import {
	IconAlertCircle,
	IconCheck,
	IconChevronDown,
	IconClock,
	IconExternalLink,
	IconFileText,
	IconMessageCircle,
	IconPaperclip,
	IconRefresh,
	IconShieldCheck,
	IconVideo,
	IconX,
} from "@tabler/icons-react"
import { useState } from "react"

import { toDateTimeString } from "@forento/core/utilities/date"
import LexicalViewer from "@forento/ui/components/LexicalViewer"
import { cn } from "@forento/ui/utilities/tailwind"

import Button from "~/components/Button"
import { useToast } from "~/contexts/ToastContext"
import { query } from "~/utilities/trpc"

export default function ModerationQueue() {
	const [kind, setKind] = useState<"post" | "comment">("post")
	const toast = useToast()
	const utils = query.useUtils()
	const queue = query.community.listModerationQueue.useInfiniteQuery(
		{ kind },
		{ getNextPageParam: page => page.nextCursor },
	)
	const review = query.community.reviewSubmission.useMutation({
		async onSuccess(_data, variables) {
			toast.showSuccess(variables.decision === "approve" ? "Submission published." : "Submission rejected.")
			await utils.community.invalidate()
		},
		onError(error) {
			toast.showError(error.message)
			void queue.refetch()
		},
	})
	const items = queue.data?.pages.flatMap(page => page.items) ?? []

	return (
		<section className="flex min-w-0 flex-col gap-5" aria-labelledby="moderation-heading">
			<header className="flex flex-wrap items-start justify-between gap-4">
				<div>
					<h2 id="moderation-heading" className="text-xl font-semibold">
						Review queue
					</h2>
					<p className="text-light mt-1 text-sm">
						A quick check before these submissions join the conversation.
					</p>
				</div>
				<Button
					variant="secondary"
					className="gap-2 px-3 py-2"
					isLoading={queue.isFetching}
					onClick={() => void queue.refetch()}
				>
					{!queue.isFetching && <IconRefresh className="size-4" aria-hidden />}
					Refresh
				</Button>
			</header>

			<div className="flex flex-wrap items-center justify-between gap-3">
				<fieldset
					className="bg-component-background inline-flex gap-1 rounded-lg p-1"
					aria-label="Submission type"
				>
					{(
						[
							{ value: "post", label: "Posts", icon: IconFileText },
							{ value: "comment", label: "Comments", icon: IconMessageCircle },
						] as const
					).map(filter => (
						<button
							key={filter.value}
							type="button"
							aria-pressed={kind === filter.value}
							onClick={() => setKind(filter.value)}
							className={cn(
								"focus-visible:outline-primary flex cursor-pointer items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2",
								kind === filter.value
									? "text-primary bg-white shadow-sm"
									: "text-light hover:text-black",
							)}
						>
							<filter.icon className="size-4" aria-hidden />
							{filter.label}
						</button>
					))}
				</fieldset>
				{queue.isSuccess && items.length > 0 && (
					<p className="text-light text-sm">
						{items.length}
						{queue.hasNextPage ? "+" : ""} awaiting review <span aria-hidden>·</span> Oldest first
					</p>
				)}
			</div>

			{queue.isPending && (
				<div
					className="border-component-border rounded-xl border bg-white p-6"
					role="status"
					aria-label="Loading submissions"
				>
					<div className="animate-pulse motion-reduce:animate-none">
						<div className="mb-6 flex items-center gap-3">
							<div className="bg-component-background size-10 rounded-full" />
							<div className="bg-component-background h-4 w-40 rounded" />
						</div>
						<div className="bg-component-background mb-3 h-5 w-2/3 rounded" />
						<div className="bg-component-background mb-2 h-3 w-full rounded" />
						<div className="bg-component-background h-3 w-4/5 rounded" />
					</div>
				</div>
			)}
			{queue.isError && (
				<div
					role="alert"
					className="border-component-border bg-component-background flex flex-col items-center rounded-xl border px-6 py-12 text-center"
				>
					<IconAlertCircle className="text-danger mb-4 size-8" aria-hidden />
					<h3 className="text-lg font-semibold">Couldn't load the queue</h3>
					<p className="text-light mt-2 mb-5 text-sm">Please try again to see submissions awaiting review.</p>
					<Button variant="secondary" onClick={() => void queue.refetch()}>
						Try again
					</Button>
				</div>
			)}
			{queue.isSuccess && items.length === 0 && (
				<div
					className="border-component-border bg-component-background flex flex-col items-center rounded-xl border px-6 py-16 text-center"
					role="status"
				>
					<div className="border-primary/10 text-primary mb-5 flex size-16 items-center justify-center rounded-2xl border bg-white">
						<IconShieldCheck className="size-8" stroke={1.5} aria-hidden />
					</div>
					<h3 className="text-xl font-semibold">You're all caught up</h3>
					<p className="text-light mt-2 max-w-sm text-sm leading-relaxed">
						No {kind === "post" ? "posts" : "comments"} awaiting review. Submissions that need your
						attention will appear here.
					</p>
				</div>
			)}

			{items.map(item => {
				const isReviewing =
					review.isPending && review.variables?.kind === kind && review.variables.id === item.id
				return (
					<article
						key={`${kind}-${item.id}`}
						className="border-component-border min-w-0 overflow-hidden rounded-xl border bg-white"
					>
						<div className="border-component-border flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4 sm:px-6">
							<div className="flex min-w-0 items-center gap-3">
								<div
									className="bg-primary/10 text-primary flex size-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold"
									aria-hidden
								>
									{item.author.firstName.slice(0, 1)}
									{item.author.lastName.slice(0, 1)}
								</div>
								<div className="min-w-0">
									<p className="text-sm font-semibold break-words">
										{item.author.firstName} {item.author.lastName}
									</p>
									<time dateTime={item.createDate.toISOString()} className="text-light text-xs">
										{toDateTimeString(item.createDate)}
									</time>
								</div>
							</div>
							<span className="bg-component-background text-light inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold">
								<IconClock className="size-3.5" aria-hidden />
								Awaiting review
							</span>
						</div>

						<div className="flex min-w-0 flex-col gap-5 p-5 sm:p-6">
							<div className="flex gap-3 rounded-lg border border-amber-200/70 bg-amber-50 p-4 text-amber-900">
								<IconAlertCircle className="mt-0.5 size-5 shrink-0" aria-hidden />
								<div className="min-w-0 text-sm">
									<p className="font-semibold">Why this needs a review</p>
									<p className="mt-1 leading-relaxed break-words">
										{item.reason ??
											"Please check this submission against your community standards."}
									</p>
								</div>
							</div>
							<div className="min-w-0">
								{kind === "post" && <p className="text-light mb-2 text-xs">Posted in {item.context}</p>}
								<h3 className="mb-3 text-lg font-semibold break-words">{item.title}</h3>
								<LexicalViewer
									html={item.htmlContent}
									className="min-w-0 overflow-x-auto text-sm leading-relaxed wrap-anywhere"
								/>
							</div>
							{kind === "comment" && (
								<details className="group bg-component-background rounded-lg px-4 py-3">
									<summary className="text-light flex cursor-pointer list-none items-center justify-between gap-2 text-sm font-semibold [&::-webkit-details-marker]:hidden">
										Original post
										<IconChevronDown
											className="size-4 transition-transform group-open:rotate-180"
											aria-hidden
										/>
									</summary>
									<p className="mt-3 max-h-64 overflow-y-auto text-sm leading-relaxed wrap-anywhere whitespace-pre-wrap">
										{item.context}
									</p>
								</details>
							)}
							{item.images.length > 0 && (
								<div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
									{item.images.map((image, index) => (
										<a
											key={image.id}
											href={image.filePath}
											target="_blank"
											rel="noreferrer"
											className="border-component-border bg-component-background focus-visible:outline-primary overflow-hidden rounded-lg border focus-visible:outline-2"
										>
											<img
												src={image.filePath}
												alt={`Submission image ${index + 1}`}
												loading="lazy"
												className="aspect-4/3 w-full object-contain"
											/>
										</a>
									))}
								</div>
							)}
							{(item.attachments.length > 0 || item.videoUrl !== null) && (
								<div className="flex flex-wrap gap-2">
									{item.attachments.map(file => (
										<a
											key={file.id}
											href={file.filePath}
											target="_blank"
											rel="noreferrer"
											className="border-component-border text-primary hover:bg-primary/5 inline-flex max-w-full items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors"
										>
											<IconPaperclip className="size-4 shrink-0" aria-hidden />
											<span className="truncate">{file.label}</span>
											<IconExternalLink className="size-3.5 shrink-0" aria-hidden />
										</a>
									))}
									{item.videoUrl !== null && (
										<a
											href={item.videoUrl}
											target="_blank"
											rel="noreferrer"
											className="border-component-border text-primary hover:bg-primary/5 inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors"
										>
											<IconVideo className="size-4" aria-hidden />
											View video
											<IconExternalLink className="size-3.5" aria-hidden />
										</a>
									)}
								</div>
							)}
							{item.pollOptions.length > 0 && (
								<div>
									<p className="text-light mb-2 text-xs font-semibold">Poll options</p>
									<ul className="flex flex-col gap-2">
										{item.pollOptions.map((option, index) => (
											<li
												key={index}
												className="border-component-border flex items-center gap-3 rounded-lg border px-3 py-2 text-sm"
											>
												<span
													className="border-component-border size-3.5 shrink-0 rounded-full border"
													aria-hidden
												/>
												<span className="wrap-anywhere">{option}</span>
											</li>
										))}
									</ul>
								</div>
							)}
						</div>

						<footer className="border-component-border bg-component-background/50 flex flex-col gap-3 border-t px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
							<p className="text-light text-xs">Only approved content is shared with members.</p>
							<div className="flex flex-wrap gap-2">
								<Button
									variant="secondary-danger"
									className="flex-1 gap-2 px-3 py-2 sm:flex-none"
									isDisabled={review.isPending}
									isLoading={isReviewing && review.variables?.decision === "reject"}
									onClick={() => review.mutate({ kind, id: item.id, decision: "reject" })}
								>
									<IconX className="size-4" aria-hidden />
									Reject
								</Button>
								<Button
									variant="primary"
									className="flex-1 gap-2 px-3 py-2 sm:flex-none"
									isDisabled={review.isPending}
									isLoading={isReviewing && review.variables?.decision === "approve"}
									onClick={() => review.mutate({ kind, id: item.id, decision: "approve" })}
								>
									<IconCheck className="size-4" aria-hidden />
									Approve & publish
								</Button>
							</div>
						</footer>
					</article>
				)
			})}
			{queue.hasNextPage && (
				<div className="flex justify-center">
					<Button
						variant="secondary"
						isLoading={queue.isFetchingNextPage}
						onClick={() => void queue.fetchNextPage()}
					>
						Load more submissions
					</Button>
				</div>
			)}
		</section>
	)
}
